import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  Button,
  Menu,
  MenuItem,
  IconButton,
  Divider
} from '@mui/material';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import 'chartjs-adapter-dayjs-4';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { 
  FileDownload as DownloadIcon,
  FilterAlt as FilterIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { fetchRides } from '../../redux/slices/ridesSlice';
import { fetchDrivers } from '../../redux/slices/driversSlice';

// Dayjs auf Deutsch einstellen
dayjs.locale('de');

// ChartJS registrieren
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Konstanten für die Datumsbereich-Filter
const FILTER_DAY = 'day';
const FILTER_WEEK = 'week';
const FILTER_MONTH = 'month';
const FILTER_QUARTER = 'quarter';
const FILTER_YEAR = 'year';

// Tabs
const TAB_OVERVIEW = 0;
const TAB_RIDES = 1;
const TAB_DRIVERS = 2;
const TAB_REVENUE = 3;

/**
 * ReportsPage-Komponente
 * 
 * Zeigt verschiedene Berichte und Visualisierungen für Fahrten, Fahrer und Umsätze an.
 */
const ReportsPage = () => {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(TAB_OVERVIEW);
  const [dateRange, setDateRange] = useState(FILTER_MONTH);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  
  // Daten aus dem Redux Store abfragen
  const { rides, isLoading: ridesLoading, error: ridesError } = useSelector(state => state.rides);
  const { drivers, isLoading: driversLoading, error: driversError } = useSelector(state => state.drivers);
  
  const isLoading = ridesLoading || driversLoading;
  const error = ridesError || driversError;
  
  // Datums-Hilfsfunktionen
  const getDateRangeStart = () => {
    const now = new Date();
    
    switch (dateRange) {
      case FILTER_DAY:
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_WEEK:
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        now.setDate(diff);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_MONTH:
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_QUARTER:
        const quarter = Math.floor(now.getMonth() / 3);
        now.setMonth(quarter * 3);
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      case FILTER_YEAR:
        now.setMonth(0);
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
      default:
        now.setDate(1);
        now.setHours(0, 0, 0, 0);
        return now;
    }
  };
  
  // Formatieren des Datumstitels für die Überschrift
  const getDateRangeTitle = () => {
    const now = new Date();
    const startDate = getDateRangeStart();
    const options = { month: 'long', year: 'numeric' };
    
    switch (dateRange) {
      case FILTER_DAY:
        return `${startDate.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      case FILTER_WEEK:
        const endOfWeek = new Date(startDate);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        return `${startDate.toLocaleDateString('de-DE', { day: 'numeric' })} - ${endOfWeek.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      case FILTER_MONTH:
        return `${startDate.toLocaleDateString('de-DE', options)}`;
      case FILTER_QUARTER:
        const quarter = Math.floor(startDate.getMonth() / 3) + 1;
        return `Q${quarter} ${startDate.getFullYear()}`;
      case FILTER_YEAR:
        return `${startDate.getFullYear()}`;
      default:
        return `${startDate.toLocaleDateString('de-DE', options)}`;
    }
  };
  
  // Daten laden
  useEffect(() => {
    const loadData = async () => {
      try {
        // Startdatum für den gewählten Zeitraum
        const startDate = getDateRangeStart();
        
        // Alle Fahrten laden mit dem korrekten Filter
        await dispatch(fetchRides({
          status: ['open', 'accepted', 'in_progress', 'arrived', 'scheduled', 
                  'pre_accepted', 'completed', 'cancelled', 'cancelled_by_driver', 'archived']
        }));
        
        // Alle Fahrer laden
        await dispatch(fetchDrivers());
        
      } catch (error) {
        console.error('Fehler beim Laden der Berichtsdaten:', error);
      }
    };
    
    loadData();
  }, [dispatch, dateRange]);
  
  // Tab-Wechsel-Handler
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  // Filter-Menü-Handler
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  const handleFilterSelect = (range) => {
    setDateRange(range);
    handleFilterClose();
  };
  
  // Export-Handler
  const handleExport = (format) => {
    // TODO: Implementieren des Exports in verschiedene Formate (CSV, PDF, Excel)
    console.log(`Export im Format ${format} wird ausgeführt...`);
  };
  
  // Druck-Handler
  const handlePrint = () => {
    window.print();
  };
  
  // Beispieldaten für die Übersichtsgrafiken
  const getOverviewChartData = () => {
    // Gesamtzahl der Fahrten
    const totalRides = rides.length;
    
    // Fahrten nach Status zählen
    const ridesByStatus = rides.reduce((acc, ride) => {
      acc[ride.status] = (acc[ride.status] || 0) + 1;
      return acc;
    }, {});
    
    // Fahrten gruppiert nach Tag für die letzten 7/30 Tage
    const today = new Date();
    const daysToShow = dateRange === FILTER_WEEK ? 7 : 30;
    
    const ridesByDay = Array(daysToShow).fill(0).map((_, index) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (daysToShow - 1 - index));
      date.setHours(0, 0, 0, 0);
      
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 1);
      
      return {
        date,
        count: rides.filter(ride => {
          if (!ride.timestamp) return false;
          const rideDate = new Date(ride.timestamp);
          return rideDate >= date && rideDate < endDate;
        }).length
      };
    });
    
    // Durschnittliche Fahrpreise berechnen
    const completedRides = rides.filter(ride => ride.status === 'completed' || ride.status === 'archived');
    const averageFare = completedRides.length > 0
      ? completedRides.reduce((sum, ride) => sum + (ride.estimatedEarningEur || ride.fare || 0), 0) / completedRides.length
      : 0;
    
    // Durschschnittliche Bewertungen pro Tag
    const ratingsByDay = Array(daysToShow).fill(0).map((_, index) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (daysToShow - 1 - index));
      date.setHours(0, 0, 0, 0);
      
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 1);
      
      const dayRides = completedRides.filter(ride => {
        if (!ride.completionTimestamp) return false;
        const rideDate = new Date(ride.completionTimestamp);
        return rideDate >= date && rideDate < endDate;
      });
      
      const averageRating = dayRides.length > 0
        ? dayRides.reduce((sum, ride) => sum + (ride.rating || 0), 0) / dayRides.length
        : 0;
      
      return {
        date,
        rating: averageRating
      };
    });
    
    return {
      totalRides,
      ridesByStatus,
      ridesByDay,
      averageFare,
      ratingsByDay
    };
  };
  
  // Übersichts-Tab-Inhalt
  const renderOverviewTab = () => {
    const { 
      totalRides, 
      ridesByStatus, 
      ridesByDay, 
      averageFare,
      ratingsByDay
    } = getOverviewChartData();
    
    // Daten für das Fahrtenstatus-Tortendiagramm
    const pieData = {
      labels: Object.keys(ridesByStatus).map(status => {
        switch (status) {
          case 'open': return 'Angefragt';
          case 'accepted': return 'Akzeptiert';
          case 'pre_accepted': return 'Vom Fahrer angenommen';
          case 'in_progress': return 'Zielfahrt';
          case 'arrived': return 'Angekommen';
          case 'completed': return 'Abgeschlossen';
          case 'archived': return 'Archiviert';
          case 'cancelled': return 'Storniert';
          case 'cancelled_by_driver': return 'Vom Fahrer storniert';
          case 'scheduled': return 'Geplant';
          default: return status;
        }
      }),
      datasets: [
        {
          data: Object.values(ridesByStatus),
          backgroundColor: [
            '#FFCE56', // Gelb für angefragt/offen
            '#36A2EB', // Blau für akzeptiert
            '#32a4a8', // Hellblau für vom Fahrer angenommen
            '#4BC0C0', // Türkis für Zielfahrt
            '#8bc34a', // Hellgrün für angekommen
            '#4CAF50', // Grün für abgeschlossen
            '#2E7D32', // Dunkelgrün für archiviert
            '#FF6384', // Rot für storniert
            '#d32f2f', // Dunkelrot für vom Fahrer storniert
            '#9C27B0'  // Lila für geplant
          ],
          borderWidth: 1
        }
      ]
    };
    
    // Optionen für das Tortendiagramm
    const pieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
        },
        title: {
          display: true,
          text: 'Fahrten nach Status'
        }
      }
    };
    
    // Daten für das Balkendiagramm der Fahrten pro Tag
    const barData = {
      labels: ridesByDay.map(data => data.date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' })),
      datasets: [
        {
          label: 'Anzahl Fahrten',
          data: ridesByDay.map(data => data.count),
          backgroundColor: '#FFD700', // Gold/Gelb für Taxis
          borderColor: '#212121', // Dunkelgrau
          borderWidth: 1
        }
      ]
    };
    
    // Optionen für das Balkendiagramm
    const barOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Fahrten pro Tag'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        }
      }
    };
    
    // Daten für das Lininiendiagramm der Bewertungen
    const lineData = {
      labels: ratingsByDay.map(data => data.date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' })),
      datasets: [
        {
          label: 'Durchschnittliche Bewertung',
          data: ratingsByDay.map(data => data.rating),
          backgroundColor: 'rgba(33, 33, 33, 0.2)',
          borderColor: '#212121',
          borderWidth: 2,
          tension: 0.3,
          fill: true
        }
      ]
    };
    
    // Optionen für das Liniendiagramm
    const lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Bewertungstrend'
        }
      },
      scales: {
        y: {
          beginAtZero: false,
          min: 0,
          max: 5,
          ticks: {
            stepSize: 1
          }
        }
      }
    };
    
    return (
      <Grid container spacing={3}>
        {/* KPI-Karten */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              color: '#212121'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {totalRides}
            </Typography>
            <Typography variant="body1">
              Fahrten gesamt
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
              color: '#FFC107'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {ridesByStatus['completed'] || 0}
            </Typography>
            <Typography variant="body1">
              Abgeschlossene Fahrten
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              color: '#212121'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {averageFare.toFixed(2)} €
            </Typography>
            <Typography variant="body1">
              Durchschnittlicher Fahrpreis
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              textAlign: 'center',
              height: '100%',
              backgroundImage: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
              color: '#FFC107'
            }}
          >
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {drivers ? drivers.filter(driver => driver.isDriver).length : 0}
            </Typography>
            <Typography variant="body1">
              Aktive Fahrer
            </Typography>
          </Paper>
        </Grid>
        
        {/* Diagramme */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Pie data={pieData} options={pieOptions} />
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Bar data={barData} options={barOptions} />
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: 300 }}>
            <Line data={lineData} options={lineOptions} />
          </Paper>
        </Grid>
      </Grid>
    );
  };
  
  // Fahrten-Tab-Inhalt
  const renderRidesTab = () => {
    // TODO: Detaillierte Fahrtenanalyse implementieren
    return (
      <Typography variant="body1">
        Fahrtenanalyse wird implementiert...
      </Typography>
    );
  };
  
  // Fahrer-Tab-Inhalt
  const renderDriversTab = () => {
    // TODO: Detaillierte Fahreranalyse implementieren
    return (
      <Typography variant="body1">
        Fahreranalyse wird implementiert...
      </Typography>
    );
  };
  
  // Umsatz-Tab-Inhalt
  const renderRevenueTab = () => {
    // TODO: Detaillierte Umsatzanalyse implementieren
    return (
      <Typography variant="body1">
        Umsatzanalyse wird implementiert...
      </Typography>
    );
  };
  
  // Hauptinhalt basierend auf dem ausgewählten Tab
  const renderTabContent = () => {
    switch (currentTab) {
      case TAB_OVERVIEW:
        return renderOverviewTab();
      case TAB_RIDES:
        return renderRidesTab();
      case TAB_DRIVERS:
        return renderDriversTab();
      case TAB_REVENUE:
        return renderRevenueTab();
      default:
        return renderOverviewTab();
    }
  };
  
  return (
    <Container maxWidth="xl">
      <Paper elevation={2} sx={{ mt: 2, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            Berichte und Analysen: {getDateRangeTitle()}
          </Typography>
          
          <Box>
            <IconButton onClick={handleFilterClick} title="Zeitraum auswählen">
              <FilterIcon />
            </IconButton>
            <IconButton onClick={handlePrint} title="Drucken">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={() => handleExport('csv')} title="Exportieren">
              <DownloadIcon />
            </IconButton>
            
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
            >
              <MenuItem onClick={() => handleFilterSelect(FILTER_DAY)}>Heute</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_WEEK)}>Diese Woche</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_MONTH)}>Dieser Monat</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_QUARTER)}>Dieses Quartal</MenuItem>
              <MenuItem onClick={() => handleFilterSelect(FILTER_YEAR)}>Dieses Jahr</MenuItem>
            </Menu>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ mb: 3 }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Übersicht" />
            <Tab label="Fahrtenanalyse" />
            <Tab label="Fahrerleistung" />
            <Tab label="Umsatzanalyse" />
          </Tabs>
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Typography variant="body1">Daten werden geladen...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ mt: 2 }}>
            <Typography color="error">
              Fehler beim Laden der Daten: {error}
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2 }} 
              onClick={() => setDateRange(dateRange)}
            >
              Erneut versuchen
            </Button>
          </Box>
        ) : (
          renderTabContent()
        )}
      </Paper>
    </Container>
  );
};

export default ReportsPage;